import React, { useEffect, useState } from 'react';
import { SkillRecord } from '@components/Component/Lesson/LessonContainer/Types';
import { StandardRecord } from '@components/ContentFilters/types';
import { useInteractionContext } from '@components/ElementAnalytics/InteractionContext';
import { useStats } from 'react-instantsearch';
import Box from '../Atoms/Box';
import { TableContext, TableDialogs } from '.';
import {
  useTableColumns,
  useAlgoliaFilters,
  useQuestionSelection,
  useTagDialog,
  usePagination,
  useTableColumnConfiguration,
} from './hooks';
import type { AvailableColumn, PageSizeOption } from './types';

export interface AlgoliaIndexTableProps {
  keyId: string; // used to re-render the component when the key changes
  availableColumns?: AvailableColumn[];
  showCheckbox?: boolean;
  hitsPerPage?: number;
  selectedTagColumns?: string[]; // prop for selected tag columns
  onTagColumnsChange?: (columns: string[]) => void; // Callback when columns change
  tableTitle?: string;
  // Add props for selected standards and skills
  selectedStandards?: StandardRecord[];
  selectedSkills?: SkillRecord[];
  selectedBloomsTaxonomy?: string[];
  selectedDepthOfKnowledge?: string[];
  questionSource: string;
  // Add the emptyStateComponent prop
  emptyStateComponent?: React.ReactNode;
  // Add external question selection props
  externalSelectedQuestions?: string[];
  onQuestionSelect?: (questionId: string, selected: boolean) => void;
  selectedReadability?: string[];
  selectedGrade?: string[];
  selectedPassageReference?: string | null;
  selectedQuestionType?: string[];
  selectedSubject?: string | null;
  showUnaligned?: boolean;
  selectButton?: (referenceId: string) => React.ReactNode;
  statUpdate?: (stats: any) => void;
}

// Constants moved outside of component for better organization
const DEFAULT_PAGE_SIZE_OPTIONS: PageSizeOption[] = [
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
];

const DEFAULT_AVAILABLE_COLUMNS: AvailableColumn[] = [
  // { key: 'Language', label: 'Language' }, // We currently only support EN
  { key: 'Standard', label: 'Standard' },
  { key: 'Skill Display', label: 'Skill' },
  { key: 'Blooms Taxonomy', label: 'Blooms Taxonomy' },
  { key: 'Depth Of Knowledge', label: 'Depth of Knowledge' },
  { key: 'Difficulty', label: 'Difficulty' },
  { key: 'Grade Levels', label: 'Grade Level' },
  { key: 'Question Type', label: 'Question Type' },
  { key: 'reference', label: 'Question ID' },
  // { key: 'Subject', label: 'Subject' }, // Current options are MATH and ELA, not very useful
];

/**
 * AlgoliaIndexTable component for displaying and interacting with Algolia search results
 */
const Table: React.FC<AlgoliaIndexTableProps> = ({
  keyId,
  availableColumns = DEFAULT_AVAILABLE_COLUMNS,
  showCheckbox = false,
  hitsPerPage = 10,
  selectedTagColumns: propSelectedColumns = [],
  onTagColumnsChange,
  tableTitle = 'Items',
  selectedStandards = [],
  selectedSkills = [],
  selectedBloomsTaxonomy = [],
  selectedDepthOfKnowledge = [],
  questionSource,
  emptyStateComponent,
  // External selection props
  externalSelectedQuestions,
  onQuestionSelect,
  selectedReadability = [],
  selectedGrade = [],
  selectedPassageReference,
  selectedQuestionType = [],
  selectedSubject,
  showUnaligned = false,
  selectButton = undefined,
  statUpdate = () => undefined,
}) => {
  // Column selector dialog state
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
  // Custom hooks for managing different aspects of the component
  const { selectedTagColumns, handleColumnsChange } = useTableColumns({
    propSelectedColumns,
    onTagColumnsChange,
  });

  // Use internal selection state and handler only if external ones aren't provided
  const {
    selectedQuestions: internalSelectedQuestions,
    handleQuestionSelect: internalHandleQuestionSelect,
  } = useQuestionSelection();

  // Determine which selection state and handler to use
  const selectedQuestions = externalSelectedQuestions || internalSelectedQuestions;
  const handleQuestionSelect = onQuestionSelect || internalHandleQuestionSelect;
  const { tagDetailsOpen, currentHit, handleTagsOpen, handleTagsClose } = useTagDialog();
  const stats = useStats();

  const facetFilters = useAlgoliaFilters({
    selectedStandards,
    selectedSkills,
    selectedBloomsTaxonomy,
    selectedDepthOfKnowledge,
    questionSource,
    passageReference: selectedPassageReference || undefined,
    readability: selectedReadability || undefined,
    grade: selectedGrade || undefined,
    selectedQuestionType: selectedQuestionType || undefined,
    subject: selectedSubject || undefined,
    showUnaligned,
  });

  const { getTestId } = useInteractionContext();
  const { currentPage, setCurrentPage, pageSize, handlePageSizeChange } = usePagination({
    initialPageSize: hitsPerPage,
    facetFilters,
  });

  const tableColumns = useTableColumnConfiguration({
    showCheckbox,
    selectedTagColumns,
    availableColumns,
  });

  useEffect(() => {
    statUpdate(stats);
  }, [statUpdate, stats]);

  return (
    <Box
      key={`${keyId}-${questionSource}`}
      sx={{ width: '100%', overflowX: 'hidden' }}
      aria-label={`${tableTitle} search results table`}
      data-testid={getTestId('question-table')}
    >
      {/* Dialogs for column selection and tag details */}
      <TableDialogs
        isColumnSelectorOpen={isColumnSelectorOpen}
        onColumnSelectorClose={() => setIsColumnSelectorOpen(false)}
        availableColumns={availableColumns}
        selectedTagColumns={selectedTagColumns}
        onColumnsChange={handleColumnsChange}
        isTagDialogOpen={tagDetailsOpen !== null}
        onTagDialogClose={handleTagsClose}
        currentHit={currentHit}
      />

      {/* Main table context */}
      <TableContext
        tableTitle={tableTitle}
        tableColumns={tableColumns}
        showCheckbox={showCheckbox}
        selectedQuestions={selectedQuestions}
        selectedTagColumns={selectedTagColumns}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        handlePageSizeChange={handlePageSizeChange}
        handleQuestionSelect={handleQuestionSelect}
        handleTagsOpen={handleTagsOpen}
        pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
        facetFilters={facetFilters}
        onConfigureColumns={() => setIsColumnSelectorOpen(true)}
        emptyStateComponent={emptyStateComponent}
        questionSource={questionSource}
        selectButton={selectButton}
      />
    </Box>
  );
};

export default Table;
